import React, { useState, useEffect } from 'react';
import ProductCard from './ProductCard';
import ShadowBox from './ShadowBox';
import './TestingPage.css';

// Each section gets its own slice so the cards are visually distinguishable and
// you can tell at a glance which section a variation actually landed in.
const slice = (products, start, count) => products.slice(start, start + count);

const Section = ({ id, number, title, root, children, note }) => (
  <section className="kt-section" id={id}>
    <h2 className="kt-section-title">
      {number} · {title} <span className="kt-root-tag">{root}</span>
    </h2>
    <p className="kt-note">{note}</p>
    {children}
  </section>
);

const Grid = ({ products, onAddToCart }) => (
  <div className="products-grid">
    {products.map((product) => (
      <ProductCard key={product.id} product={product} onAddToCart={onAddToCart} />
    ))}
  </div>
);

// Section 6 — mounts only after a delay, imitating a hydration-late component.
const LateGrid = ({ products, onAddToCart }) => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setReady(true), 1200);
    return () => clearTimeout(timer);
  }, []);

  if (!ready) {
    return <div className="kt-pending">Upgrading in 1200 ms…</div>;
  }

  return (
    <ShadowBox mode="open" className="kt-host" data-kt-host="late">
      <Grid products={products} onAddToCart={onAddToCart} />
    </ShadowBox>
  );
};

// Section 7 — re-renders its own subtree on a timer. Anything Kameleoon wrote
// into these nodes gets discarded on each pass unless the change is re-applied.
const ChurningGrid = ({ products, onAddToCart }) => {
  const [pass, setPass] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setPass((p) => p + 1), 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <ShadowBox mode="open" className="kt-host" data-kt-host="churn">
      <div className="kt-churn-meta">re-render #{pass}</div>
      <div className="products-grid">
        {products.map((product) => (
          // Keying on the pass forces a genuine remount, not a reconciled update.
          <ProductCard
            key={`${product.id}-${pass}`}
            product={product}
            onAddToCart={onAddToCart}
          />
        ))}
      </div>
    </ShadowBox>
  );
};

const TestingPage = ({ products, onAddToCart }) => {
  if (!products.length) {
    return <div className="kt-page"><p className="kt-note">Loading products…</p></div>;
  }

  return (
    <div className="kt-page">
      <header className="kt-header">
        <h1>Shadow DOM test harness</h1>
        <p className="kt-note">
          The same <code>ProductCard</code> component rendered behind seven different DOM
          boundaries. Every section is styled identically on purpose — if a Kameleoon
          variation, the graphic editor, or a selector behaves differently between them,
          the boundary is the only variable.
        </p>
      </header>

      <Section
        id="kt-light"
        number="1"
        title="Light DOM control group"
        root="no shadow"
        note="Ordinary markup, no shadow root. If a selector fails here too, the problem is not Shadow DOM and you are debugging something else."
      >
        <Grid products={slice(products, 0, 3)} onAddToCart={onAddToCart} />
      </Section>

      <Section
        id="kt-open"
        number="2"
        title="Open shadow root"
        root="mode: open"
        note="host.shadowRoot is reachable from any script. Tests whether Kameleoon's selector engine traverses into a shadow root at all, and whether the graphic editor can pick these cards."
      >
        <ShadowBox mode="open" className="kt-host" data-kt-host="open">
          <Grid products={slice(products, 0, 3)} onAddToCart={onAddToCart} />
        </ShadowBox>
      </Section>

      <Section
        id="kt-slotted"
        number="3"
        title="Open shadow root with slotted light DOM"
        root="mode: open + slot"
        note="The chrome lives in the shadow root; the heading and badge are projected in from the light DOM through a slot. Slotted content is reachable by ordinary selectors — shadow chrome is not. Also exposes ::part(badge) and the --card-accent custom property for CSS-only targeting."
      >
        <ShadowBox
          mode="open"
          className="kt-host"
          data-kt-host="slotted"
          lightChildren={
            <>
              <h3 slot="heading" className="kt-slotted-heading">Slotted heading (light DOM)</h3>
              <span slot="badge" className="kt-slotted-badge">Editable normally</span>
            </>
          }
        >
          <div className="kt-slot-frame">
            <slot name="heading" />
            <span part="badge" className="kt-part-badge">::part(badge) — shadow chrome</span>
            <slot name="badge" />
            <Grid products={slice(products, 3, 3)} onAddToCart={onAddToCart} />
          </div>
        </ShadowBox>
      </Section>

      <Section
        id="kt-nested"
        number="4"
        title="Nested shadow roots, two levels deep"
        root="mode: open × 2"
        note="A shadow root containing a host with its own shadow root. If traversal handles level one but not level two, this is where it shows up."
      >
        <ShadowBox mode="open" className="kt-host" data-kt-host="nested-outer">
          <div className="kt-nested-frame">
            <span className="kt-level-tag">level 1</span>
            <ShadowBox mode="open" className="kt-host" data-kt-host="nested-inner">
              <span className="kt-level-tag">level 2</span>
              <Grid products={slice(products, 0, 2)} onAddToCart={onAddToCart} />
            </ShadowBox>
          </div>
        </ShadowBox>
      </Section>

      <Section
        id="kt-closed"
        number="5"
        title="Closed shadow root"
        root="mode: closed"
        note="Attached with mode: 'closed', so host.shadowRoot is null for every script on the page. The expected result is a clean failure, not a partial one — this is here so you can tell “Kameleoon can't do it” apart from “the web platform won't allow it.”"
      >
        <ShadowBox mode="closed" className="kt-host" data-kt-host="closed">
          <Grid products={slice(products, 3, 3)} onAddToCart={onAddToCart} />
        </ShadowBox>
      </Section>

      <Section
        id="kt-late"
        number="6"
        title="Late-mounted shadow root"
        root="mode: open, +1200 ms"
        note="Mounts 1200 ms after page load. Tests the timing question: does the engine wait for the node to appear, and does the applied change flicker?"
      >
        <LateGrid products={slice(products, 0, 3)} onAddToCart={onAddToCart} />
      </Section>

      <Section
        id="kt-churn"
        number="7"
        title="Self-rewriting shadow root"
        root="mode: open, remounts every 3 s"
        note="Remounts its cards every three seconds. Anything written into these nodes is discarded on each pass unless the change is re-applied. This is the case that separates a one-shot DOM write from a resilient variation."
      >
        <ChurningGrid products={slice(products, 3, 2)} onAddToCart={onAddToCart} />
      </Section>

      <Section
        id="kt-matrix"
        number="8"
        title="What to record for each case"
        root="reference"
        note="Run each row through the graphic editor, then through a code variation, and note where they diverge."
      >
        <table className="kt-matrix">
          <thead>
            <tr>
              <th>#</th>
              <th>Host</th>
              <th>Root</th>
              <th>What it proves</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>1</td>
              <td><code>#kt-light</code></td>
              <td>none</td>
              <td>Control group — baseline selector and editor behaviour</td>
            </tr>
            <tr>
              <td>2</td>
              <td><code>[data-kt-host="open"]</code></td>
              <td>open</td>
              <td>Whether the selector engine crosses a shadow boundary at all</td>
            </tr>
            <tr>
              <td>3</td>
              <td><code>[data-kt-host="slotted"]</code></td>
              <td>open + slot</td>
              <td>Slotted light DOM should be editable normally; shadow chrome is the real test. Also <code>::part()</code> and custom-property styling</td>
            </tr>
            <tr>
              <td>4</td>
              <td><code>[data-kt-host="nested-inner"]</code></td>
              <td>open, nested</td>
              <td>Whether traversal survives a second shadow boundary</td>
            </tr>
            <tr>
              <td>5</td>
              <td><code>[data-kt-host="closed"]</code></td>
              <td>closed</td>
              <td>Expected hard stop — confirms the platform limit rather than a product one</td>
            </tr>
            <tr>
              <td>6</td>
              <td><code>[data-kt-host="late"]</code></td>
              <td>open, delayed</td>
              <td>Mount timing and flicker</td>
            </tr>
            <tr>
              <td>7</td>
              <td><code>[data-kt-host="churn"]</code></td>
              <td>open, remounting</td>
              <td>Whether a variation survives the component rewriting itself</td>
            </tr>
          </tbody>
        </table>
      </Section>
    </div>
  );
};

export default TestingPage;
